import { useCallback, useEffect, useState } from 'react';
import type { Sessao } from '../../auth';
import { Icone } from '../../design/Icone';
import { fecharMes, lerFechamento, reais, registrarPagamento } from '../../metas';
import type { FechamentoDoMes } from '../../metas';
import { Esqueleto } from '../equipe/Equipe';

/**
 * FECHAMENTO DO MÊS E PAGAMENTO (dono, 29/09 — fase 5). Mês aberto: a prévia do que seria
 * confirmado. Fechar relê o HOS, recalcula e TRAVA (projetado → confirmado; não reabre). Depois,
 * marcar como pago com data e motivo (ninguém paga a si mesmo). Tudo decidido no servidor.
 */

const MESES = [
  'janeiro',
  'fevereiro',
  'março',
  'abril',
  'maio',
  'junho',
  'julho',
  'agosto',
  'setembro',
  'outubro',
  'novembro',
  'dezembro',
];
const nomeDoMes = (c: string) => `${MESES[Number(c.slice(5, 7)) - 1] ?? ''}/${c.slice(0, 4)}`;
const ddmmaa = (d: string | null) =>
  d ? `${d.slice(8, 10)}/${d.slice(5, 7)}/${d.slice(0, 4)}` : '—';
function mesesParaEscolher(): string[] {
  const hoje = new Date().toLocaleDateString('sv-SE', { timeZone: 'America/Sao_Paulo' });
  const [a, m] = hoje.split('-').map(Number);
  return Array.from({ length: 6 }, (_, k) =>
    new Date(Date.UTC(a ?? 2026, (m ?? 1) - 1 - k, 1)).toISOString().slice(0, 7),
  );
}
const hojeISO = () => new Date().toLocaleDateString('sv-SE', { timeZone: 'America/Sao_Paulo' });

export function Fechamento({
  sessao,
  podeFechar,
  podePagar,
}: {
  sessao: Sessao;
  podeFechar: boolean;
  podePagar: boolean;
}) {
  const meses = mesesParaEscolher();
  const [competencia, setCompetencia] = useState(meses[1] ?? meses[0] ?? '');
  const [d, setD] = useState<FechamentoDoMes | null>(null);
  const [erro, setErro] = useState(false);
  const [motivo, setMotivo] = useState('');
  const [enviando, setEnviando] = useState(false);
  const [msg, setMsg] = useState<{ ok: boolean; texto: string } | null>(null);
  const [marcados, setMarcados] = useState<Set<string>>(new Set());
  const [pagoEm, setPagoEm] = useState(hojeISO());
  const [comoPagou, setComoPagou] = useState('');

  const carregar = useCallback(() => {
    setErro(false);
    setD(null);
    setMarcados(new Set());
    void lerFechamento(competencia).then((r) => (r.ok ? setD(r.dados) : setErro(true)));
  }, [competencia]);
  useEffect(carregar, [carregar]);

  const falha = (r: { tipo: string; mensagem?: string }) =>
    r.tipo === 'recusada'
      ? (r.mensagem ?? 'Não foi aceito.')
      : 'Sem resposta do servidor — confira antes de repetir.';

  return (
    <section className="mp-fechamento">
      <div className="mp-linha">
        <label className="mp-campo mp-campo-curto">
          <span>Mês</span>
          <select value={competencia} onChange={(e) => setCompetencia(e.target.value)}>
            {meses.map((c) => (
              <option key={c} value={c}>
                {nomeDoMes(c)}
              </option>
            ))}
          </select>
        </label>
        {d && (
          <span
            className={`mp-status ${d.estado === 'FECHADA' ? 'mp-status-ativa' : 'mp-status-pausada'}`}
          >
            {d.estado === 'FECHADA' ? 'Fechado — valores travados' : 'Aberto — bônus projetado'}
          </span>
        )}
      </div>

      {erro && <p className="mp-falha">Não foi possível carregar o mês.</p>}
      {!erro && !d && <Esqueleto linhas={4} />}

      {d?.estado === 'ABERTA' && (
        <>
          <p className="ac-nota">
            Prévia do que seria confirmado se o mês fechasse agora. Ao fechar, o sistema relê as
            vendas finais do HOS, recalcula com a regra que valia em {nomeDoMes(d.competencia)} e
            trava tudo — depois nenhuma mudança de regra mexe neste mês.
          </p>
          <TabelaPrevia d={d} />
          {podeFechar ? (
            d.podeFechar ? (
              <div className="mp-linha mp-fechar">
                <label className="mp-campo">
                  <span>Motivo do fechamento</span>
                  <input
                    value={motivo}
                    onChange={(e) => setMotivo(e.target.value)}
                    placeholder="ex.: fechamento de setembro conferido com o Carlos"
                  />
                </label>
                <button
                  type="button"
                  className="ac-botao ac-botao-primario"
                  disabled={enviando || motivo.trim().length < 5}
                  onClick={() => {
                    setEnviando(true);
                    setMsg(null);
                    void fecharMes(sessao, d.competencia, motivo).then((r) => {
                      setEnviando(false);
                      if (r.tipo === 'confirmada') {
                        setMsg({
                          ok: true,
                          texto: `Mês fechado: ${String(r.dados.balconistas)} balconista(s), ${reais(r.dados.total)} confirmados.`,
                        });
                        setMotivo('');
                        carregar();
                      } else setMsg({ ok: false, texto: falha(r) });
                    });
                  }}
                >
                  <Icone nome="lock" tamanho={14} traco={2} />{' '}
                  {enviando ? 'Fechando… (relendo o HOS)' : 'Fechar o mês'}
                </button>
              </div>
            ) : (
              <p className="ac-nota">O mês só fecha depois do último dia.</p>
            )
          ) : (
            <p className="ac-nota">Fechar o mês é de quem cuida do dinheiro das metas (o dono).</p>
          )}
        </>
      )}

      {d?.estado === 'FECHADA' && (
        <>
          <p className="ac-nota">
            Fechado em{' '}
            {d.fechadaEm
              ? new Date(d.fechadaEm).toLocaleString('pt-BR', {
                  timeZone: 'America/Sao_Paulo',
                  day: '2-digit',
                  month: '2-digit',
                  year: 'numeric',
                  hour: '2-digit',
                  minute: '2-digit',
                })
              : '—'}
            {d.fechadaPor ? ` por ${d.fechadaPor}` : ''} · "{d.motivo}" · pagamento previsto{' '}
            {ddmmaa(d.pagamentoPrevisto)}
          </p>
          <div className="ac-cartao ac-tabela-rolo">
            <table className="ac-tabela" data-sem-ordenar>
              <thead>
                <tr>
                  {podePagar && <th aria-label="Marcar" />}
                  <th>Balconista</th>
                  <th className="ac-num">Pelas metas</th>
                  <th className="ac-num">Acima do teto</th>
                  <th className="ac-num">Confirmado</th>
                  <th>Pagamento</th>
                </tr>
              </thead>
              <tbody>
                {d.linhas.map((l) => (
                  <tr key={l.accountId}>
                    {podePagar && (
                      <td>
                        {!l.pagoEm && Number(l.confirmado) > 0 && (
                          <input
                            type="checkbox"
                            aria-label={`Marcar ${l.nome}`}
                            checked={marcados.has(l.accountId)}
                            onChange={() =>
                              setMarcados((s) => {
                                const n = new Set(s);
                                if (n.has(l.accountId)) n.delete(l.accountId);
                                else n.add(l.accountId);
                                return n;
                              })
                            }
                          />
                        )}
                      </td>
                    )}
                    <td>{l.nome}</td>
                    <td className="ac-num">{reais(l.pelasMetas)}</td>
                    <td className="ac-num">{Number(l.excedente) > 0 ? reais(l.excedente) : '—'}</td>
                    <td className="ac-num">
                      <b className="mp-valor">{reais(l.confirmado)}</b>
                    </td>
                    <td>
                      {l.pagoEm ? (
                        <span className="mp-status mp-status-ativa">
                          Pago {ddmmaa(l.pagoEm)}
                          {l.pagoPor ? ` · ${l.pagoPor}` : ''}
                        </span>
                      ) : Number(l.confirmado) > 0 ? (
                        <span className="mp-status mp-status-pausada">A pagar</span>
                      ) : (
                        <span className="mp-sem-premio">sem bônus</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {podePagar && marcados.size > 0 && (
            <div className="mp-linha mp-fechar">
              <label className="mp-campo mp-campo-mini">
                <span>Pago em</span>
                <input type="date" value={pagoEm} onChange={(e) => setPagoEm(e.target.value)} />
              </label>
              <label className="mp-campo">
                <span>Como foi pago</span>
                <input
                  value={comoPagou}
                  onChange={(e) => setComoPagou(e.target.value)}
                  placeholder="ex.: PIX no dia 05"
                />
              </label>
              <button
                type="button"
                className="ac-botao ac-botao-primario"
                disabled={enviando || comoPagou.trim().length < 5}
                onClick={() => {
                  setEnviando(true);
                  setMsg(null);
                  void registrarPagamento(sessao, {
                    competencia: d.competencia,
                    contas: [...marcados],
                    pagoEm,
                    motivo: comoPagou,
                  }).then((r) => {
                    setEnviando(false);
                    if (r.tipo === 'confirmada') {
                      setMsg({
                        ok: true,
                        texto: `${String(r.dados.pagos)} pagamento(s) registrado(s): ${reais(r.dados.total)}.`,
                      });
                      setComoPagou('');
                      carregar();
                    } else setMsg({ ok: false, texto: falha(r) });
                  });
                }}
              >
                <Icone nome="money" tamanho={14} traco={1.8} /> Marcar {marcados.size} como pago
              </button>
            </div>
          )}
        </>
      )}
      {msg && (
        <p className={msg.ok ? 'mp-ok' : 'mp-falha'} role={msg.ok ? 'status' : 'alert'}>
          {msg.texto}
        </p>
      )}
    </section>
  );
}

function TabelaPrevia({ d }: { d: FechamentoDoMes }) {
  if (!d.previa.length)
    return <p className="ac-nota">Nenhum balconista com meta que paga neste mês.</p>;
  return (
    <div className="ac-cartao ac-tabela-rolo">
      <table className="ac-tabela" data-sem-ordenar>
        <thead>
          <tr>
            <th>Balconista</th>
            <th className="ac-num">Pelas metas</th>
            <th className="ac-num">Acima do teto</th>
            <th className="ac-num">Bônus projetado</th>
          </tr>
        </thead>
        <tbody>
          {d.previa.map((l) => (
            <tr key={l.accountId}>
              <td>{l.nome}</td>
              <td className="ac-num">{reais(l.pelasMetas)}</td>
              <td className="ac-num">{Number(l.excedente) > 0 ? reais(l.excedente) : '—'}</td>
              <td className="ac-num">
                <b>{reais(l.projetado)}</b>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
